import React, { useMemo, useRef, useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useHistory } from "react-router-dom";
import useCommonQuery from "../../store/common/useCommonQuery";
import Header from "../../components/header/Header";
import BottomNav from "../../components/Bottom nav/BottomNav";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CampaignIcon from "@mui/icons-material/Campaign";
import LanguageIcon from "@mui/icons-material/Language";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ShareIcon from "@mui/icons-material/Share";
import HomeIcon from "@mui/icons-material/Home";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CategoryIcon from "@mui/icons-material/Category";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StorefrontIcon from "@mui/icons-material/Storefront";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FlightIcon from "@mui/icons-material/Flight";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import MovieIcon from "@mui/icons-material/Movie";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MemoryIcon from "@mui/icons-material/Memory";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import NearMeIcon from "@mui/icons-material/NearMe";
import "./InfluencersPage.css";
import InfluencerSocialButtons from '../../components/InfluencerSocialButtons/InfluencerSocialButtons';

/* ═══════════════════════════════════════════════════════════════
   HAVERSINE DISTANCE CALCULATION
   Returns distance in kilometers between two lat/lng points
═══════════════════════════════════════════════════════════════ */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* ═══════════════════════════════════════════════════════════════
   FORMAT DISTANCE - converts km to readable format
═══════════════════════════════════════════════════════════════ */
function formatDistance(km) {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
}

/* ─────────────────────────────────────────────────────────────────
   Category config — maps raw category string → color + icon
───────────────────────────────────────────────────────────────── */
const CAT_CONFIGS = [
  {
    keys: ["business","promotion","brand","market","commerce","entrepreneur","startup","shop","store","product"],
    label: "BSNSS", color: "#fb923c",
    solid: "rgba(249,115,22,0.82)", glow: "rgba(249,115,22,0.55)",
    border: "rgba(255,180,100,0.60)", shine: "rgba(255,210,150,0.35)",
    bg: "linear-gradient(160deg,rgba(249,115,22,0.55) 0%,rgba(180,83,9,0.70) 100%)",
    icon: (sz) => <StorefrontIcon sx={{ fontSize: sz }} />,
  },
  {
    keys: ["health","wellness","medical","doctor","fitness","yoga","nutrition","hospital","clinic","pharma","ayur"],
    label: "HELTH", color: "#34d399",
    solid: "rgba(16,185,129,0.82)", glow: "rgba(16,185,129,0.55)",
    border: "rgba(100,230,180,0.60)", shine: "rgba(150,255,210,0.35)",
    bg: "linear-gradient(160deg,rgba(16,185,129,0.55) 0%,rgba(6,95,70,0.70) 100%)",
    icon: (sz) => <HealthAndSafetyIcon sx={{ fontSize: sz }} />,
  },
  {
    keys: ["travel","tourism","tour","trip","adventure","hotel","resort","destination","flight","holiday"],
    label: "TRVL", color: "#38bdf8",
    solid: "rgba(14,165,233,0.82)", glow: "rgba(14,165,233,0.55)",
    border: "rgba(100,210,255,0.60)", shine: "rgba(160,235,255,0.35)",
    bg: "linear-gradient(160deg,rgba(14,165,233,0.55) 0%,rgba(3,105,161,0.70) 100%)",
    icon: (sz) => <FlightIcon sx={{ fontSize: sz }} />,
  },
  {
    keys: ["news","update","media","journal","report","press","current","affairs","broadcast"],
    label: "NEWS", color: "#f87171",
    solid: "rgba(239,68,68,0.82)", glow: "rgba(239,68,68,0.55)",
    border: "rgba(255,150,150,0.60)", shine: "rgba(255,200,200,0.35)",
    bg: "linear-gradient(160deg,rgba(239,68,68,0.55) 0%,rgba(153,27,27,0.70) 100%)",
    icon: (sz) => <NewspaperIcon sx={{ fontSize: sz }} />,
  },
  {
    keys: ["entertain","movie","film","music","comedy","actor","actress","cinema","art","dance","drama","show"],
    label: "ENTR", color: "#a78bfa",
    solid: "rgba(168,85,247,0.82)", glow: "rgba(168,85,247,0.55)",
    border: "rgba(200,150,255,0.60)", shine: "rgba(220,180,255,0.35)",
    bg: "linear-gradient(160deg,rgba(168,85,247,0.55) 0%,rgba(109,40,217,0.70) 100%)",
    icon: (sz) => <MovieIcon sx={{ fontSize: sz }} />,
  },
  {
    keys: ["tech","software","digital","computer","ai","app","web","coding","developer","startup","gadget","device","electronic","circuit","chip"],
    label: "TECH", color: "#22d3ee",
    solid: "rgba(6,182,212,0.82)", glow: "rgba(6,182,212,0.55)",
    border: "rgba(100,230,245,0.60)", shine: "rgba(160,245,255,0.35)",
    bg: "linear-gradient(160deg,rgba(6,182,212,0.55) 0%,rgba(14,116,144,0.70) 100%)",
    icon: (sz) => <MemoryIcon sx={{ fontSize: sz }} />,
  },
];

function getCatConfig(raw) {
  const lower = (raw || "").toLowerCase();
  const matched = CAT_CONFIGS.find((c) => c.keys.some((k) => lower.includes(k)));
  if (matched) return matched;
  return {
    label: (raw || "CAT").slice(0, 5).toUpperCase(),
    color: "#38bdf8",
    solid: "rgba(14,165,233,0.82)",
    glow: "rgba(14,165,233,0.55)",
    border: "rgba(100,210,255,0.60)",
    shine: "rgba(160,235,255,0.35)",
    bg: "linear-gradient(160deg,rgba(14,165,233,0.55) 0%,rgba(3,105,161,0.70) 100%)",
    icon: (sz) => <CategoryIcon sx={{ fontSize: sz }} />,
  };
}

/* ─────────────────────────────────────────────────────────────────
   Category badge — vertical crystal pill (right side of card)
───────────────────────────────────────────────────────────────── */
function CategoryBadge({ category }) {
  const cfg = getCatConfig(category);
  return (
    <div
      className="cat-badge"
      style={{
        background: cfg.bg,
        boxShadow: `0 0 14px ${cfg.glow}, 0 0 5px ${cfg.glow}, inset 0 1px 0 ${cfg.shine}`,
        border: `1.5px solid ${cfg.border}`,
      }}
    >
      <div className="cat-badge-sheen" />
      <div className="cat-badge-inner-glow" style={{ boxShadow: `inset 0 0 10px ${cfg.glow}` }} />
      <span className="cat-badge-icon" style={{ color: cfg.color }}>{cfg.icon(11)}</span>
      <span className="cat-badge-label">{cfg.label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Category name icon — small badge before influencer name
───────────────────────────────────────────────────────────────── */
function CategoryNameIcon({ category }) {
  if (!category) return null;
  const cfg = getCatConfig(category);
  return (
    <span
      className="pc-cat-icon"
      style={{
        background: "linear-gradient(160deg,#0a1a3a 0%,#0d2255 100%)",
        boxShadow: `0 0 8px ${cfg.glow}, inset 0 1px 0 rgba(255,210,150,0.25)`,
        border: `1.5px solid ${cfg.border}`,
      }}
    >
      <div className="pc-cat-icon-sheen" />
      <span className="pc-cat-icon-inner">
        <span style={{ color: cfg.color, display: "inline-flex", filter: `drop-shadow(0 0 4px ${cfg.glow})` }}>
          {cfg.icon(7)}
        </span>
        <span className="pc-cat-icon-label">{category}</span>
      </span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────
   STEP badge palette  — 7 premium neon-glow gradients
───────────────────────────────────────────────────────────────── */
const STEP_PALETTE = [
  {
    bg: "linear-gradient(180deg, #1e90ff 0%, #0047c8 100%)",
    glow: "rgba(30,144,255,0.70)",
    border: "rgba(100,190,255,0.55)",
    shine: "rgba(160,220,255,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #a855f7 0%, #6d28d9 100%)",
    glow: "rgba(168,85,247,0.70)",
    border: "rgba(200,150,255,0.55)",
    shine: "rgba(220,180,255,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #f97316 0%, #b45309 100%)",
    glow: "rgba(249,115,22,0.70)",
    border: "rgba(255,180,100,0.55)",
    shine: "rgba(255,210,150,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #10b981 0%, #065f46 100%)",
    glow: "rgba(16,185,129,0.70)",
    border: "rgba(100,230,180,0.55)",
    shine: "rgba(150,255,210,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #ec4899 0%, #9d174d 100%)",
    glow: "rgba(236,72,153,0.70)",
    border: "rgba(255,150,210,0.55)",
    shine: "rgba(255,190,230,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #06b6d4 0%, #0e7490 100%)",
    glow: "rgba(6,182,212,0.70)",
    border: "rgba(100,230,245,0.55)",
    shine: "rgba(160,245,255,0.40)",
  },
  {
    bg: "linear-gradient(180deg, #f59e0b 0%, #92400e 100%)",
    glow: "rgba(245,158,11,0.70)",
    border: "rgba(255,210,100,0.55)",
    shine: "rgba(255,235,160,0.40)",
  },
];

const DYNAMIC_CATEGORY_PALETTES = [
  { color: "#fb923c", glow: "rgba(249,115,22,0.40)", border: "rgba(255,180,100,0.50)", icon: (sz) => <StorefrontIcon sx={{ fontSize: sz, color: "#fb923c" }} /> },
  { color: "#34d399", glow: "rgba(16,185,129,0.40)", border: "rgba(100,230,180,0.50)", icon: (sz) => <HealthAndSafetyIcon sx={{ fontSize: sz, color: "#34d399" }} /> },
  { color: "#38bdf8", glow: "rgba(14,165,233,0.40)", border: "rgba(100,210,255,0.50)", icon: (sz) => <FlightIcon sx={{ fontSize: sz, color: "#38bdf8" }} /> },
  { color: "#f87171", glow: "rgba(239,68,68,0.40)", border: "rgba(255,150,150,0.50)", icon: (sz) => <NewspaperIcon sx={{ fontSize: sz, color: "#f87171" }} /> },
  { color: "#a78bfa", glow: "rgba(168,85,247,0.40)", border: "rgba(200,150,255,0.50)", icon: (sz) => <MovieIcon sx={{ fontSize: sz, color: "#a78bfa" }} /> },
  { color: "#22d3ee", glow: "rgba(6,182,212,0.40)", border: "rgba(100,230,245,0.50)", icon: (sz) => <MemoryIcon sx={{ fontSize: sz, color: "#22d3ee" }} /> },
  { color: "#f43f5e", glow: "rgba(244,63,94,0.40)", border: "rgba(251,113,133,0.50)", icon: (sz) => <FavoriteIcon sx={{ fontSize: sz, color: "#f43f5e" }} /> },
  { color: "#eab308", glow: "rgba(234,179,8,0.40)", border: "rgba(250,204,21,0.50)", icon: (sz) => <EmojiEventsIcon sx={{ fontSize: sz, color: "#eab308" }} /> },
];

/* ─────────────────────────────────────────────────────────────────
   Animated stat card
───────────────────────────────────────────────────────────────── */
function StatCard({ icon, value, label, color, delay = 0 }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const t = setTimeout(() => el.classList.add("psc-visible"), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div ref={ref} className="psc" style={{ "--glow": color }}>
      <div className="psc-glow-ring" />
      <div className="psc-icon" style={{ color }}>{icon}</div>
      <div className="psc-value">{value}</div>
      <div className="psc-label">{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   STEP badge  — vertical crystal pill
───────────────────────────────────────────────────────────────── */
function StepBadge({ rank, palette }) {
  const rankStr = String(rank).padStart(2, "0");
  return (
    <div
      className="step-badge"
      style={{
        background: palette.bg,
        boxShadow: `0 0 18px ${palette.glow}, 0 0 6px ${palette.glow}, inset 0 1px 0 ${palette.shine}`,
        border: `1.5px solid ${palette.border}`,
      }}
    >
      {/* Crystal top sheen */}
      <div className="step-badge-sheen" />
      {/* Inner glow ring */}
      <div className="step-badge-inner-glow" style={{ boxShadow: `inset 0 0 10px ${palette.glow}` }} />
      <span className="step-badge-num">{rankStr}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Social icon button
───────────────────────────────────────────────────────────────── */
function SocialBtn({ href, icon, colorClass }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" className={`psoc ${colorClass}`}>
      <div className="psoc-gloss" />
      {icon}
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Influencer card  — MOBILE: Photo LEFT | Buttons BELOW Photo | Name/Category RIGHT | Description
   DESKTOP: Original layout preserved
───────────────────────────────────────────────────────────────── */
function wrapAt30(text) {
  const flat = text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
  const chars = Array.from(flat);
  const lines = [];
  for (let i = 0; i < chars.length; i += 35) {
    lines.push(chars.slice(i, i + 35).join(''));
  }
  return lines;
}

function InfluencerCard({ item, rank, visible, distance }) {
  const palette = STEP_PALETTE[(rank - 1) % STEP_PALETTE.length];
  const categoryName = item.category_detail?.name || (typeof item.category === "string" ? item.category : "");

  return (
    <div className={`pc ${visible ? "pc-visible" : ""}`}>
      {/* Card top-edge crystal sheen */}
      <div className="pc-sheen" />

      {/* ── LEFT: profile photo ── */}
      <div className="pc-photo-wrap">
        {/* Soft blue bloom behind image */}
        <div className="pc-photo-bloom" style={{ background: `radial-gradient(circle, ${palette.glow} 0%, transparent 70%)` }} />
        <div
          className="pc-photo-frame"
          style={{
            boxShadow: `0 0 0 2px ${palette.border}, 0 0 22px ${palette.glow}, 0 6px 24px rgba(0,0,0,0.60)`,
          }}
        >
          {item.image ? (
            <img src={item.image} alt={item.name} className="pc-photo" />
          ) : (
            <div className="pc-photo-placeholder">
              <PersonIcon sx={{ fontSize: 40, color: "rgba(255,255,255,0.40)" }} />
            </div>
          )}
          <div className="pc-photo-glow" style={{ background: `radial-gradient(circle at 25% 25%, ${palette.shine} 0%, transparent 60%)` }} />
        </div>
        {item.is_verified && (
          <div className="pc-verified" style={{ boxShadow: `0 0 10px rgba(30,144,255,0.90)` }}>
            <VerifiedIcon sx={{ fontSize: 11, color: "#fff" }} />
          </div>
        )}

        {/* ── BUTTONS DIRECTLY BELOW PHOTO (shared standardized component) ── */}
        <InfluencerSocialButtons
          contact_number={item.contact_number}
          facebook_link={item.facebook_link}
          instagram_link={item.instagram_link}
          youtube_link={item.youtube_link}
          name={item.name}
        />
      </div>

      {/* ── RIGHT SIDE: Name (top) + Category (far right) ── */}
      <div className="pc-controls">
        {/* Name row — top edge aligned with photo top */}
        <div className="pc-controls-name">
          <span className="pc-name">{item.name}</span>
          {item.is_verified && (
            <VerifiedIcon sx={{ fontSize: 13, color: "#38bdf8", flexShrink: 0 }} />
          )}

        </div>
        {/* Description - positioned to the right of photo, below name */}
        {item.description && (() => {
          const chunks = wrapAt30(item.description).slice(0, 6);
          const lineStyle = {
            display: 'block',
            whiteSpace: 'pre',
            overflow: 'hidden',
            textOverflow: 'clip',
            fontSize: '0.58rem',
            lineHeight: '1.55',
            color: 'rgba(185,215,250,0.65)',
            textAlign: 'left',
            textAlignLast: 'left',
          };
          return (
            <p className="pc-controls-desc pc-controls-desc-mobile"
               style={{ display: 'block', margin: 0, padding: 0 }}>
              {chunks.map((chunk, idx) => (
                <span key={idx} style={lineStyle}>{chunk}</span>
              ))}
            </p>
          );
        })()}
      </div>

      {/* ── CENTER: info (category, description, location — name moved to controls) ── */}
      <div className="pc-info">
        {/* Category icon badge */}
        <CategoryNameIcon category={categoryName} />

        {/* Subtitle / profession */}
        {item.subtitle && (
          <p className="pc-subtitle">{item.subtitle}</p>
        )}

        {/* Category — now rendered in pc-controls-name, top-aligned with name */}


        {/* Location + Distance */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {item.district && (
            <div className="pc-meta pc-meta-loc">
              <LocationOnIcon sx={{ fontSize: 10 }} />
              <span>{item.district}</span>
            </div>
          )}
          {distance !== null && distance !== undefined && (
            <div className="pc-meta pc-meta-distance" style={{ 
              background: "linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(6,182,212,0.10) 100%)",
              border: "1px solid rgba(34,211,238,0.30)",
              color: "#22d3ee",
              boxShadow: "0 0 8px rgba(34,211,238,0.25)"
            }}>
              <NearMeIcon sx={{ fontSize: 10 }} />
              <span>{formatDistance(distance)} away</span>
            </div>
          )}
        </div>

        {/* Bottom row removed — moved to pc-photo-wrap */}
      </div>

      {/* ── RIGHT: STEP badge ── */}
      <div className="pc-right">
        <StepBadge rank={rank} palette={palette} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Influencers page
───────────────────────────────────────────────────────────────── */
function InfluencersPage({ items, history }) {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState("prompt"); // 'prompt' | 'granted' | 'denied'
  const [locationError, setLocationError] = useState(null);

  const { data: catData } = useCommonQuery.Get_influencer_categories();
  const catList = useMemo(() => {
    const d = catData;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.results)) return d.results;
    if (Array.isArray(d?.data?.results)) return d.data.results;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [catData]);

  // Request user's current location on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn("[InfluencersPage] Geolocation not supported");
      setLocationPermission("denied");
      return;
    }

    // Check if we already have permission
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          requestLocation();
        } else if (result.state === "denied") {
          setLocationPermission("denied");
        }
      }).catch(() => {
        // Fallback: try requesting anyway
        requestLocation();
      });
    } else {
      // Fallback for browsers without Permissions API
      requestLocation();
    }
  }, []);

  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationPermission("granted");
        setLocationError(null);
        console.log("[InfluencersPage] User location obtained:", { latitude, longitude });
      },
      (error) => {
        console.warn("[InfluencersPage] Location permission denied or error:", error.message);
        setLocationPermission("denied");
        setLocationError(error.message);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  };

  // Filter, calculate distance, and sort items
  const filteredItems = useMemo(() => {
    let result = items;
    
    // 1. Filter by category
    if (selectedCategory !== "all") {
      result = result.filter((item) => {
        const itemCatId = item.category !== null && item.category !== undefined ? String(item.category) : "";
        const itemCatDetailName = (item.category_detail?.name || "").toLowerCase().trim();
        const itemCatName = (typeof item.category === "string" ? item.category : "").toLowerCase().trim();
        const target = String(selectedCategory).toLowerCase().trim();
        return itemCatId === target || itemCatDetailName === target || itemCatName === target;
      });
    }
    
    // 2. Calculate distance for each item if user location is available
    result = result.map((item) => {
      let distance = null;
      
      if (userLocation && item.latitude && item.longitude) {
        try {
          distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            parseFloat(item.latitude),
            parseFloat(item.longitude)
          );
        } catch (err) {
          console.warn("[InfluencersPage] Distance calculation error for", item.name, err);
        }
      }
      
      return { ...item, distance };
    });
    
    // 3. Sort by Admin Star Rating (primary) + Distance (secondary)
    result = [...result].sort((a, b) => {
      const ratingA = a.star_rating || 0;
      const ratingB = b.star_rating || 0;
      
      // Primary: Star rating (highest first)
      if (ratingB !== ratingA) {
        return ratingB - ratingA; // 5 stars → 4 stars → 3 stars → etc.
      }
      
      // Secondary: Distance (nearest first) - only for items with same star rating
      const distA = a.distance;
      const distB = b.distance;
      
      // Both have distance - sort by nearest
      if (distA !== null && distB !== null) {
        return distA - distB; // Ascending (nearest first)
      }
      
      // Only A has distance - A comes first
      if (distA !== null && distB === null) {
        return -1;
      }
      
      // Only B has distance - B comes first
      if (distA === null && distB !== null) {
        return 1;
      }
      
      // Neither has distance - maintain original order (stable sort)
      return 0;
    });
    
    return result;
  }, [items, selectedCategory, userLocation]);

  const stats = useMemo(() => {
    const platforms = new Set();
    let verified = 0;
    let active = 0;
    let totalReach = 0;
    filteredItems.forEach((item) => {
      if (item.whatsapp_link) platforms.add("whatsapp");
      if (item.youtube_link) platforms.add("youtube");
      if (item.instagram_link) platforms.add("instagram");
      if (item.facebook_link) platforms.add("facebook");
      if (item.is_verified) verified++;
      if (item.is_active !== false) active++;
      if (item.reach) totalReach += Number(item.reach) || 0;
    });
    return {
      total: filteredItems.length,
      verified,
      active,
      platforms: platforms.size || 4,
      reach:
        totalReach > 0
          ? totalReach >= 1000
            ? `${(totalReach / 1000).toFixed(1)}K`
            : String(totalReach)
          : `${filteredItems.length * 5}K+`,
    };
  }, [filteredItems]);

  /* Staggered card reveal */
  useEffect(() => {
    setVisibleCards(new Set()); // Reset visible cards when filtered items change
    filteredItems.forEach((_, i) => {
      setTimeout(() => {
        setVisibleCards((prev) => new Set([...prev, i]));
      }, 80 + i * 65);
    });
  }, [filteredItems]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  return (
    <div className="ip-page">

      {/* ══════════ HERO ══════════ */}
      <div className="ip-hero">
        {/* Ambient light orbs */}
        <div className="ip-orb ip-orb-a" />
        <div className="ip-orb ip-orb-b" />
        <div className="ip-orb ip-orb-c" />
        <div className="ip-orb ip-orb-d" />
        <div className="ip-orb ip-orb-e" />

        {/* Glass card */}
        <div className="ip-hero-card">
          {/* Top sheen */}
          <div className="ip-hero-sheen" />

          {/* Title row — title left, nav icons right */}
          <div className="ip-hero-title-row">
            <div className="ip-hero-titles">
              <div className="ip-hero-brand">
                <div className="ip-hero-icon-wrap">
                  <div className="ip-hero-icon-glow" />
                  <LanguageIcon sx={{ fontSize: 18, color: "#fff", position: "relative", zIndex: 1 }} />
                </div>
                <h1 className="ip-hero-title">
                  <span className="ip-hero-title-neon">Master Click Influencers</span>
                </h1>
              </div>
            </div>
            <div className="ip-hero-nav-btns">
              <button className="ip-nav-btn" onClick={() => history.push('/')} aria-label="Home">
                <div className="ip-nav-btn-glow" />
                <HomeIcon sx={{ fontSize: 15, color: "#fff", position: "relative", zIndex: 1 }} />
              </button>
              <button className="ip-nav-btn" onClick={() => { if (navigator.share) navigator.share({ title: 'Master Click Influencers', url: window.location.href }); }} aria-label="Share">
                <div className="ip-nav-btn-glow" />
                <ShareIcon sx={{ fontSize: 15, color: "#fff", position: "relative", zIndex: 1 }} />
              </button>
            </div>
          </div>

          {/* Subtitle */}
          <p className="ip-hero-sub">
            Discover trusted local influencers across Kerala. Connect with
            verified creators and explore their social reach from one platform.
          </p>

          {/* Stats grid — 2 cards only */}
          <div className="ip-stats-grid ip-stats-grid--two">
            <StatCard
              icon={<PeopleAltIcon sx={{ fontSize: 13 }} />}
              value={stats.total}
              label="Total Influencers"
              color="#38bdf8"
              delay={100}
            />
            <StatCard
              icon={<TrendingUpIcon sx={{ fontSize: 13 }} />}
              value={stats.reach}
              label="Total Reach"
              color="#fb923c"
              delay={200}
            />
          </div>

          {/* Category filter buttons — each with its own color */}

          <div className="ip-cat-row">
            {/* ALL — spans full 3-column width */}
            <button 
              className={`ip-cat-btn ip-cat-btn--all ${selectedCategory === "all" ? "ip-cat-btn-active" : ""}`}
              onClick={() => handleCategoryClick("all")}
              style={{ "--cat-color": "#38bdf8", "--cat-glow": "rgba(56,189,248,0.40)", "--cat-border": "rgba(100,210,255,0.50)" }}
            >
              <CategoryIcon sx={{ fontSize: 14, color: "#38bdf8" }} />
              <span>All</span>
            </button>
            {/* Dynamic categories from database */}
            {catList.map((cat, idx) => {
              const pal = DYNAMIC_CATEGORY_PALETTES[idx % DYNAMIC_CATEGORY_PALETTES.length];
              const isSelected =
                String(selectedCategory) === String(cat.id) ||
                String(selectedCategory).toLowerCase() === cat.name.toLowerCase();
              return (
                <button
                  key={cat.id}
                  className={`ip-cat-btn ${isSelected ? "ip-cat-btn-active" : ""}`}
                  onClick={() => handleCategoryClick(cat.id)}
                  style={{
                    "--cat-color": pal.color,
                    "--cat-glow": pal.glow,
                    "--cat-border": pal.border,
                  }}
                >
                  {pal.icon(14)}
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tagline strip */}
        <div className="ip-tagline">
          <span className="ip-tagline-dot" />
          <span>Kerala's Premier Influencer Discovery Platform</span>
          <span className="ip-tagline-dot" />
        </div>
      </div>

      {/* ══════════ PHOTO STRIP CAROUSEL ══════════ */}
      {items.length > 0 && (
        <div className="ip-strip-wrap">
          <div className="ip-strip-track">
            {[...items, ...items].map((item, idx) => (
              <div key={idx} className="ip-strip-item">
                {item.image
                  ? <img src={item.image} alt={item.name} className="ip-strip-img" />
                  : <div className="ip-strip-placeholder"><PersonIcon sx={{ fontSize: 10, color: "rgba(255,255,255,0.40)" }} /></div>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════ SECTION HEADER ══════════ */}
      <div className="ip-section-head">
        <div className="ip-section-line" />
        <span className="ip-section-label">
          {selectedCategory === "all"
            ? "ALL INFLUENCERS"
            : `${(catList.find((c) => String(c.id) === String(selectedCategory) || c.name.toLowerCase() === String(selectedCategory).toLowerCase())?.name || String(selectedCategory)).toUpperCase()} INFLUENCERS`}
        </span>
        <div className="ip-section-line" />
      </div>

      {/* ══════════ CARDS ══════════ */}
      {filteredItems.length === 0 ? (
        <div className="ip-empty">
          {selectedCategory === "all" 
            ? "No influencers found." 
            : `No influencers found in ${(catList.find((c) => String(c.id) === String(selectedCategory) || c.name.toLowerCase() === String(selectedCategory).toLowerCase())?.name || selectedCategory)} category.`}
        </div>
      ) : (
        <div className="ip-cards">
          {filteredItems.map((item, idx) => (
            <InfluencerCard
              key={item.id}
              item={item}
              rank={idx + 1}
              visible={visibleCards.has(idx)}
              distance={item.distance}
            />
          ))}
        </div>
      )}

      {/* ══════════ LOCATION PERMISSION NOTICE ══════════ */}
      {locationPermission === "denied" && locationError && (
        <div className="ip-location-notice" style={{
          background: "linear-gradient(135deg, rgba(251,146,60,0.10) 0%, rgba(249,115,22,0.05) 100%)",
          border: "1px solid rgba(251,146,60,0.25)",
          borderRadius: 12,
          padding: "12px 16px",
          margin: "0 12px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          boxShadow: "0 0 20px rgba(251,146,60,0.15)",
        }}>
          <MyLocationIcon sx={{ fontSize: 20, color: "#fb923c", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ 
              fontSize: "0.75rem", 
              color: "rgba(255,255,255,0.85)", 
              margin: 0, 
              lineHeight: 1.4 
            }}>
              Enable location to see nearby influencers first
            </p>
          </div>
          <button
            onClick={requestLocation}
            style={{
              background: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              color: "#fff",
              fontSize: "0.70rem",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 0 12px rgba(251,146,60,0.40)",
              flexShrink: 0,
            }}
          >
            Enable
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Shield SVG emblem
───────────────────────────────────────────────────────────────── */
function RecShieldEmblem() {
  return (
    <svg className="rec-shield-svg" viewBox="0 0 140 115" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Pedestal glow */}
      <ellipse cx="70" cy="109" rx="32" ry="6" fill="rgba(0,200,255,0.30)" />
      <rect x="54" y="97" width="32" height="9" rx="3.5" fill="url(#rsPedA)" />
      <rect x="46" y="104" width="48" height="6" rx="3" fill="url(#rsPedB)" />

      {/* Left laurel */}
      <g opacity="0.82">
        <path d="M28 88 C20 82 14 72 16 62" stroke="url(#rsLaurelL)" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <ellipse cx="22" cy="90" rx="5" ry="3" transform="rotate(-30 22 90)" fill="url(#rsLeafL)" opacity="0.9"/>
        <ellipse cx="18" cy="82" rx="5" ry="3" transform="rotate(-45 18 82)" fill="url(#rsLeafL)" opacity="0.85"/>
        <ellipse cx="15" cy="73" rx="5" ry="3" transform="rotate(-55 15 73)" fill="url(#rsLeafL)" opacity="0.80"/>
        <ellipse cx="14" cy="63" rx="5" ry="3" transform="rotate(-60 14 63)" fill="url(#rsLeafL)" opacity="0.75"/>
        <ellipse cx="16" cy="54" rx="4" ry="2.5" transform="rotate(-50 16 54)" fill="url(#rsLeafL)" opacity="0.65"/>
      </g>

      {/* Right laurel */}
      <g opacity="0.82">
        <path d="M112 88 C120 82 126 72 124 62" stroke="url(#rsLaurelR)" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <ellipse cx="118" cy="90" rx="5" ry="3" transform="rotate(30 118 90)" fill="url(#rsLeafR)" opacity="0.9"/>
        <ellipse cx="122" cy="82" rx="5" ry="3" transform="rotate(45 122 82)" fill="url(#rsLeafR)" opacity="0.85"/>
        <ellipse cx="125" cy="73" rx="5" ry="3" transform="rotate(55 125 73)" fill="url(#rsLeafR)" opacity="0.80"/>
        <ellipse cx="126" cy="63" rx="5" ry="3" transform="rotate(60 126 63)" fill="url(#rsLeafR)" opacity="0.75"/>
        <ellipse cx="124" cy="54" rx="4" ry="2.5" transform="rotate(50 124 54)" fill="url(#rsLeafR)" opacity="0.65"/>
      </g>

      {/* Shield body */}
      <path d="M70 6 L104 21 L104 57 C104 77 88 92 70 98 C52 92 36 77 36 57 L36 21 Z"
        fill="url(#rsShieldBody)" stroke="url(#rsShieldEdge)" strokeWidth="1.5"/>
      {/* Inner glass highlight */}
      <path d="M70 12 L98 25 L98 56 C98 73 84 86 70 92 C56 86 42 73 42 56 L42 25 Z"
        fill="url(#rsInner)" opacity="0.45"/>
      {/* Top-left crystal sheen */}
      <path d="M70 12 L42 25 L42 48 C48 38 58 28 70 22 Z"
        fill="rgba(255,255,255,0.12)" />

      {/* Gold star */}
      <polygon
        points="70,26 74,37 86,37 76.5,44 80,55 70,48 60,55 63.5,44 54,37 66,37"
        fill="url(#rsStarGrad)"
        filter="url(#rsStarGlow)"
      />

      <defs>
        <linearGradient id="rsShieldBody" x1="36" y1="6" x2="104" y2="98" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1a56e8"/>
          <stop offset="50%" stopColor="#0d3aaa"/>
          <stop offset="100%" stopColor="#071b4d"/>
        </linearGradient>
        <linearGradient id="rsShieldEdge" x1="36" y1="6" x2="104" y2="98" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#66e3ff"/>
          <stop offset="60%" stopColor="#38bdf8"/>
          <stop offset="100%" stopColor="#1e6acc"/>
        </linearGradient>
        <linearGradient id="rsInner" x1="42" y1="12" x2="98" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="rgba(255,255,255,0.18)"/>
          <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
        </linearGradient>
        <linearGradient id="rsStarGrad" x1="54" y1="26" x2="86" y2="55" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fef08a"/>
          <stop offset="45%" stopColor="#f59e0b"/>
          <stop offset="100%" stopColor="#b45309"/>
        </linearGradient>
        <linearGradient id="rsPedA" x1="54" y1="97" x2="86" y2="106" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e40af"/><stop offset="100%" stopColor="#1d4ed8"/>
        </linearGradient>
        <linearGradient id="rsPedB" x1="46" y1="104" x2="94" y2="110" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e3a8a"/><stop offset="100%" stopColor="#1d4ed8"/>
        </linearGradient>
        <linearGradient id="rsLaurelL" x1="28" y1="88" x2="16" y2="62" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.3"/>
        </linearGradient>
        <linearGradient id="rsLaurelR" x1="112" y1="88" x2="124" y2="62" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.6"/>
          <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.3"/>
        </linearGradient>
        <linearGradient id="rsLeafL" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#93c5fd"/><stop offset="100%" stopColor="#38bdf8"/>
        </linearGradient>
        <linearGradient id="rsLeafR" x1="0" y1="0" x2="1" y2="0" gradientUnits="objectBoundingBox">
          <stop offset="0%" stopColor="#38bdf8"/><stop offset="100%" stopColor="#93c5fd"/>
        </linearGradient>
        <filter id="rsStarGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Recognition stat card
───────────────────────────────────────────────────────────────── */
function RecStatCard({ icon, value, label, color, delay = 0 }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const t = setTimeout(() => el.classList.add("psc-visible"), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div ref={ref} className="psc" style={{ "--glow": color }}>
      <div className="psc-glow-ring" />
      <div className="psc-icon" style={{ color }}>{icon}</div>
      <div className="psc-value">{value}</div>
      <div className="psc-label">{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Recognition hero section
───────────────────────────────────────────────────────────────── */
function RecognitionHero({ items, history }) {
  const stats = useMemo(() => {
    let govt = 0, pub = 0, org = 0, pres = 0;
    items.forEach((item) => {
      const cat = (item.category || "").toLowerCase();
      if (cat.includes("collector") || cat.includes("ias") || cat.includes("government") || cat.includes("mla") || cat.includes("mp")) govt++;
      else if (cat.includes("panchayat") || cat.includes("president") || cat.includes("public")) pub++;
      else if (cat.includes("org") || cat.includes("institution") || cat.includes("ngo")) org++;
      else pres++;
    });
    return { total: items.length, govt: govt || 0, pub: pub || 0, org: org || 0, pres: pres || 0 };
  }, [items]);

  return (
    <div className="rec-hero">
      <div className="ip-orb ip-orb-a" />
      <div className="ip-orb ip-orb-b" />
      <div className="ip-orb ip-orb-c" />
      <div className="ip-orb ip-orb-d" />
      <div className="ip-orb ip-orb-e" />

      <div className="ip-hero-card" style={{ position: "relative" }}>
        <div className="ip-hero-sheen" />

        {/* Title row */}
        <div className="ip-hero-title-row">
          <div className="ip-hero-titles">
            <h1 className="ip-hero-title" style={{ fontSize: "0.88rem", lineHeight: 1.18 }}>
              Master Click<br />
              <span className="ip-hero-title-neon" style={{ fontSize: "1.10rem" }}>Recognition</span>
            </h1>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div className="rec-shield-wrap">
              <div className="rec-shield-glow" />
              <RecShieldEmblem />
            </div>
            <div className="rec-five-stars">
              {[1,2,3,4,5].map(i => (
                <span key={i} className="rec-five-stars-star">★</span>
              ))}
            </div>
            <div className="ip-hero-nav-btns" style={{ marginTop: 4 }}>
              <button className="ip-nav-btn" onClick={() => { if (navigator.share) navigator.share({ title: "Master Click Recognition", url: window.location.href }); }} aria-label="Share">
                <div className="ip-nav-btn-glow" />
                <ShareIcon sx={{ fontSize: 15, color: "#fff", position: "relative", zIndex: 1 }} />
              </button>
              <button className="ip-nav-btn" onClick={() => history.goBack()} aria-label="Back">
                <div className="ip-nav-btn-glow" />
                <ArrowBackIosNewIcon sx={{ fontSize: 12, color: "#fff", position: "relative", zIndex: 1 }} />
              </button>
            </div>
          </div>
        </div>

        <p className="ip-hero-sub" style={{ fontSize: "0.62rem", lineHeight: 1.45, marginBottom: 8, maxWidth: "68%", marginTop: "-57px" }}>
          Recognitions and appreciations received from Government officials,
          Local Self Government Institutions, Public Representatives,
          Organizations, and Community Leaders for serving society through
          the Master Click platform.
        </p>

        {/* Stats — 5 cards in a scrollable row */}
        <div className="rec-stats-row">
          <RecStatCard icon={<GroupsIcon sx={{ fontSize: 20 }} />}       value={stats.total} label="Total Recognitions"        color="#38bdf8" delay={100} />
          <RecStatCard icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />} value={stats.govt}  label="Government Recognitions"   color="#34d399" delay={180} />
          <RecStatCard icon={<PeopleAltIcon sx={{ fontSize: 20 }} />}    value={stats.pub}   label="Public Appreciations"      color="#a78bfa" delay={260} />
          <RecStatCard icon={<EmojiEventsIcon sx={{ fontSize: 20 }} />}  value={stats.org}   label="Organizations"             color="#fbbf24" delay={340} />
          <RecStatCard icon={<CampaignIcon sx={{ fontSize: 20 }} />}     value={stats.pres}  label="Presentations"             color="#f87171" delay={420} />
        </div>
      </div>

      <div className="ip-tagline">
        <span className="ip-tagline-dot" />
        <span>Honored by leaders who believe in our mission</span>
        <span className="ip-tagline-dot" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Others card  (original design preserved)
───────────────────────────────────────────────────────────────── */
const socialIconStyle = {
  width: 30, height: 30, borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", fontSize: 13, fontWeight: "bold", color: "#fff", textDecoration: "none",
};

function RecognitionNumBadge({ rank }) {
  const palette = STEP_PALETTE[(rank - 1) % STEP_PALETTE.length];
  const rankStr = String(rank).padStart(2, "0");
  return (
    <span
      className="rec-num-badge"
      style={{
        background: palette.bg,
        boxShadow: `0 0 10px ${palette.glow}, inset 0 1px 0 ${palette.shine}`,
        border: `1.5px solid ${palette.border}`,
      }}
    >
      <span className="rec-num-badge-sheen" />
      <span className="rec-num-badge-inner" style={{ boxShadow: `inset 0 0 8px ${palette.glow}` }} />
      <span className="rec-num-badge-star">★</span>
      <span className="rec-num-badge-num">{rankStr}</span>
    </span>
  );
}

function ExtraItemCard({ item, rank }) {
  return (
    <div className="rec-item-card">
      <div className="rec-item-sheen" />
      <div className="rec-item-photo-wrap">
        {item.image
          ? <img src={item.image} alt={item.name} className="rec-item-photo" />
          : <div className="rec-item-photo-placeholder"><GroupsIcon sx={{ fontSize: 30, color: "rgba(100,180,255,0.50)" }} /></div>
        }
        <div className="rec-item-photo-ring" />
      </div>
      <div className="rec-item-body">
        <div className="rec-item-name-row">
          <p className="rec-item-name">{item.name}</p>
          <RecognitionNumBadge rank={rank} />
        </div>
        {item.description && <p className="rec-item-desc">{item.description}</p>}
        {item.contact_number && (
          <a href={`tel:${item.contact_number}`} className="rec-item-phone">
            <PhoneIcon sx={{ fontSize: 10 }} />{item.contact_number}
          </a>
        )}
        <div className="rec-item-socials">
          {item.whatsapp_link  && <a href={item.whatsapp_link}  target="_blank" rel="noreferrer" className="psoc psoc-wa"><div className="psoc-gloss" /><WhatsAppIcon  sx={{ fontSize: 12 }} /></a>}
          {item.youtube_link   && <a href={item.youtube_link}   target="_blank" rel="noreferrer" className="psoc psoc-yt"><div className="psoc-gloss" /><YouTubeIcon   sx={{ fontSize: 12 }} /></a>}
          {item.instagram_link && <a href={item.instagram_link} target="_blank" rel="noreferrer" className="psoc psoc-ig"><div className="psoc-gloss" /><InstagramIcon sx={{ fontSize: 12 }} /></a>}
          {item.facebook_link  && <a href={item.facebook_link}  target="_blank" rel="noreferrer" className="psoc psoc-fb"><div className="psoc-gloss" /><FacebookIcon  sx={{ fontSize: 12 }} /></a>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Root export
───────────────────────────────────────────────────────────────── */
function ExtraItemsPage({ isInfluencer }) {
  const history = useHistory();
  const { data, isLoading } = useCommonQuery.Get_extra_items({ is_influencer: isInfluencer });
  const items = data?.results || data?.data?.results || data?.data || [];

  return (
    <div className="ip-root">
      <Box sx={{ pt: "0" }}>
        {isLoading ? (
          <div className="ip-loader-wrap">
            <div className="ip-loader" />
            <span className="ip-loader-text">Loading…</span>
          </div>
        ) : isInfluencer ? (
          <InfluencersPage items={items} history={history} />
        ) : (
          <div className="ip-page">
            <RecognitionHero items={items} history={history} />

            <div className="ip-section-head">
              <div className="ip-section-line" />
              <span className="ip-section-label">OUR RECOGNITIONS</span>
              <div className="ip-section-line" />
            </div>

            {/* Recognition image carousel */}
            {items.length > 0 && (
              <div className="rec-strip-wrap">
                <div className="rec-strip-track">
                  {[...items, ...items].map((item, idx) => (
                    <div key={idx} className="rec-strip-item">
                      {item.image
                        ? <img src={item.image} alt={item.name} className="rec-strip-img" />
                        : <div className="rec-strip-placeholder"><GroupsIcon sx={{ fontSize: 12, color: "rgba(100,180,255,0.50)" }} /></div>
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Box sx={{ px: "12px", pb: "80px" }}>
              {items.length === 0 ? (
                <Typography variant="body2" textAlign="center" sx={{ mt: 4, color: "rgba(200,225,255,0.45)" }}>
                  No recognitions found.
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {items.map((item, idx) => (
                    <Grid item xs={6} key={item.id}>
                      <ExtraItemCard item={item} rank={idx + 1} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </div>
        )}
      </Box>

      <BottomNav />
    </div>
  );
}

export default ExtraItemsPage;
